import * as Sentry from '@sentry/react';

const ACCESS_TOKEN_KEY = 'metadevprofile_access_token';
const REFRESH_TOKEN_KEY = 'metadevprofile_refresh_token';
const EXPIRES_AT_KEY = 'metadevprofile_expires_at';

class TokenStorage {
  private cachedAccessToken: string | null = null;
  private cachedRefreshToken: string | null = null;
  private cachedExpiresAt: number | null = null;
  private initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      this.cachedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      this.cachedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      const expiresAtStr = localStorage.getItem(EXPIRES_AT_KEY);
      this.cachedExpiresAt = expiresAtStr ? Number.parseInt(expiresAtStr, 10) : null;
      this.initialized = true;
    } catch (error) {
      Sentry.captureException(error);
      this.cachedAccessToken = null;
      this.cachedRefreshToken = null;
      this.cachedExpiresAt = null;
      this.initialized = true;
    }
  }

  async getAccessToken(): Promise<string | null> {
    if (!this.initialized) await this.initialize();
    return this.cachedAccessToken;
  }

  async setAccessToken(token: string): Promise<void> {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    this.cachedAccessToken = token;
  }

  async clearAccessToken(): Promise<void> {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    this.cachedAccessToken = null;
  }

  async getRefreshToken(): Promise<string | null> {
    if (!this.initialized) await this.initialize();
    return this.cachedRefreshToken;
  }

  async setRefreshToken(token: string): Promise<void> {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
    this.cachedRefreshToken = token;
  }

  async clearRefreshToken(): Promise<void> {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    this.cachedRefreshToken = null;
  }

  async setExpiresAt(expiresIn: number): Promise<void> {
    // Remove 30s for margin
    const expiresAt = Date.now() + (expiresIn - 30) * 1000;
    localStorage.setItem(EXPIRES_AT_KEY, expiresAt.toString());
    this.cachedExpiresAt = expiresAt;
  }

  async getExpiresAt(): Promise<number | null> {
    if (!this.initialized) await this.initialize();
    return this.cachedExpiresAt;
  }

  async clearExpiresAt(): Promise<void> {
    localStorage.removeItem(EXPIRES_AT_KEY);
    this.cachedExpiresAt = null;
  }

  async updateTokens(accessToken: string, refreshToken?: string, expiresIn?: number): Promise<void> {
    await this.setAccessToken(accessToken);
    if (refreshToken) await this.setRefreshToken(refreshToken);
    if (expiresIn) await this.setExpiresAt(expiresIn);
  }

  async clearAllTokens(): Promise<void> {
    await Promise.all([this.clearAccessToken(), this.clearRefreshToken(), this.clearExpiresAt()]);
  }

  async hasTokens(): Promise<boolean> {
    const accessToken = await this.getAccessToken();
    const refreshToken = await this.getRefreshToken();
    return !!(accessToken && refreshToken);
  }
}

export const tokenStorage = new TokenStorage();
